import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from './api';

const CurrencyContext = createContext(null);

const CURRENCY_STORAGE_KEY = 'preferred_currency';

export const CurrencyProvider = ({ children }) => {
  const [currencies, setCurrencies] = useState([]);
  const [selectedCurrency, setSelectedCurrency] = useState('USD');
  const [rates, setRates] = useState({ USD: 1 });
  const [isLoading, setIsLoading] = useState(true);

  // Load currencies and rates
  const loadCurrencies = useCallback(async () => {
    try {
      const response = await api.get('/currencies');
      const activeCurrencies = response.data.currencies.filter(c => c.is_active);
      setCurrencies(activeCurrencies);
      
      // Build rates object
      const ratesObj = {};
      activeCurrencies.forEach(c => {
        ratesObj[c.code] = c.rate;
      });
      setRates(ratesObj);
      
      // Restore saved preference or use default
      const savedCurrency = localStorage.getItem(CURRENCY_STORAGE_KEY);
      if (savedCurrency && ratesObj[savedCurrency]) {
        setSelectedCurrency(savedCurrency);
      } else {
        setSelectedCurrency(response.data.default_currency || 'USD');
      }
    } catch (error) {
      console.error('Failed to load currencies:', error);
      // Fallback to USD
      setCurrencies([{ 
        code: 'USD', 
        symbol: '$', 
        name: 'US Dollar', 
        position: 'before',
        rate: 1 
      }]);
      setRates({ USD: 1 });
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCurrencies();
  }, [loadCurrencies]);

  // Change currency
  const changeCurrency = useCallback((currencyCode) => {
    if (rates[currencyCode]) {
      setSelectedCurrency(currencyCode);
      localStorage.setItem(CURRENCY_STORAGE_KEY, currencyCode);
    }
  }, [rates]);

  // Convert price from USD to selected currency
  const convertPrice = useCallback((priceInUSD) => {
    const rate = rates[selectedCurrency] || 1;
    return priceInUSD * rate;
  }, [selectedCurrency, rates]);

  // Always "$X/RY"; appends the selected currency when it is neither USD nor ZAR
  const formatUsdZar = useCallback((priceInUSD) => {
    const usd = Number(priceInUSD) || 0;
    let result = `$${formatAmount(usd, 'USD')}`;
    if (rates.ZAR) result += `/R${formatAmount(usd * rates.ZAR, 'ZAR')}`;
    const currency = currencies.find(c => c.code === selectedCurrency);
    if (currency && !['USD', 'ZAR'].includes(selectedCurrency)) {
      const amount = formatAmount(usd * (rates[selectedCurrency] || 1), selectedCurrency);
      result += currency.position === 'before' ? ` (${currency.symbol}${amount})` : ` (${amount} ${currency.symbol})`;
    }
    return result;
  }, [selectedCurrency, currencies, rates]);

  const formatPrice = formatUsdZar;

  // Format amount based on currency (some currencies don't use decimals)
  const formatAmount = (amount, currencyCode) => {
    // Currencies that typically don't use decimal places
    const noDecimalCurrencies = ['JPY', 'KRW'];
    
    if (noDecimalCurrencies.includes(currencyCode)) {
      return Math.round(amount).toLocaleString();
    }
    
    return amount.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  // Get currency info
  const getCurrencyInfo = useCallback((code = selectedCurrency) => {
    return currencies.find(c => c.code === code) || null;
  }, [selectedCurrency, currencies]);

  const value = {
    currencies,
    selectedCurrency,
    rates,
    isLoading,
    changeCurrency,
    convertPrice,
    formatPrice,
    formatUsdZar,
    getCurrencyInfo,
    refreshRates: loadCurrencies
  };

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
};

export const Usd = ({ value }) => {
  const { formatUsdZar } = useCurrency();
  return formatUsdZar(value);
};

export const PriceCaption = ({ className = '' }) => (
  <span className={`block text-xs italic mt-1 ${className}`} style={{ color: '#8B9187' }} data-testid="price-conversion-caption">
    Please use Google to convert USD to know your currency
  </span>
);

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
};

export default CurrencyContext;
